<template>
  <BaseModule
    v-bind="$props"
    class="photos-mosaic-module"
    :class="{ loading: loading }"
    data-module="photos-mosaic"
  >
    <Loader v-if="loading" />

    <Warning
      v-else-if="editing && !blocks.length"
      text="O módulo não possui imagens selecionadas ou importadas da integração"
    >
    </Warning>

    <div
      ref="gallery"
      class="grid-imagens"
      :data-alternate-state="alternateState"
      @mouseenter="stopItemAlternation"
      @mouseleave="startItemAlternation"
    >
      <div
        v-for="(block, index) in blocks"
        :key="index"
        class="grid-block"
      >
        <div
          v-for="(item, idx) in block"
          :key="idx"
          class="grid-item"
          :data-type="item.type"
        >
          <div
            v-if="item.type !== 'text'"
            :data-src="mediaHref(item)"
            class="imagem"
            :data-sub-html="item.caption"
            :title="item.alt"
          >
            <figure
              class="full-bg b-lazy"
              :style="{ backgroundImage: `url('${mediaSrc(item)}')` }"
            >
              <img
                :src="mediaSrc(item)"
                :alt="item.alt"
              />
            </figure>
          </div>
          <div
            v-else
            class="text-item"
            :style="{ background: item.bgColor || '#fff' }"
          >
            <p
              class="text-item__title"
              :style="{ color: item.titleColor || '#000' }"
            >
              {{ item.title }}
            </p>
            <p
              class="text-item__text"
              :style="{ color: item.textColor || '#000' }"
            >
              {{ item.text }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';
import 'lightgallery.js';
import 'lg-video.js';

import getApiHotels from '@/mixins/getApiHotels';
import Loader from '@/components/Loader.vue';
import Warning from '@/components/Warning.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'PhotosMosaic',
  components: {
    BaseModule,
    Loader,
    Warning,
  },
  mixins: [getApiHotels],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    media: {
      type: Array,
      default() {
        return [];
      },
    },
    loadImagesFromLetsBook: {
      type: Boolean,
      default: false,
    },
    hotelId: {
      type: Number,
      default: undefined,
    },
    alternateItems: {
      type: Boolean,
      default: false,
    },
    alternateItemsSpeed: {
      type: [String, Number],
      default: 8,
    },
  },
  data() {
    return {
      letsBookImages: [],
      alternateState: false,
      interval: null,
      loading: false,
    };
  },
  computed: {
    editing() {
      // TODO: encontrar uma forma de limpar o hotelId ao desmarcar
      // o loadImagesFromLetsbook
      return typeof this.$parent === 'undefined';
    },
    lightGallery() {
      const uid = this.$refs.gallery.getAttribute('lg-uid');
      return window.lgData[uid];
    },
    blocks() {
      const allItems = [...this.letsBookImages, ...this.media];

      if (!this.alternateItems) {
        return allItems.map((item) => [item]);
      }

      const numberOfBlocks = Math.min(Math.ceil(allItems.length / 2), 8);
      const firstHalf = allItems.slice(0, numberOfBlocks);

      return firstHalf.map((item, idx) => {
        const alternateItem = allItems[idx + numberOfBlocks];
        return alternateItem ? [item, alternateItem] : [item];
      });
    },
    ...mapGetters(['settings', 'hasLetsbookIntegration']),
  },
  watch: {
    loadImagesFromLetsBook(shouldLoad) {
      if (shouldLoad && this.hotelId) {
        this.requestImagesFromLetsBook();
      } else {
        this.letsBookImages = [];
      }
    },
    hotelId(hotelId) {
      if (this.loadImagesFromLetsBook && hotelId) {
        this.requestImagesFromLetsBook();
      }
    },
    blocks() {
      this.$nextTick(() => {
        this.destroyGallery();
        this.initializeGallery();
      });
    },
    alternateItems(checked) {
      if (checked) {
        this.startItemAlternation();
      } else {
        this.stopItemAlternation();
      }
    },
    alternateItemsSpeed() {
      this.stopItemAlternation();
      this.startItemAlternation();
    },
  },
  mounted() {
    if (this.hasLetsbookIntegration) {
      this.getApiHotels();
    }

    if (this.loadImagesFromLetsBook && this.hotelId) {
      this.requestImagesFromLetsBook();
    }

    this.initializeGallery();

    if (this.alternateItems) {
      this.startItemAlternation();
    }
  },
  beforeDestroy() {
    this.destroyGallery();
    this.stopItemAlternation();
  },
  methods: {
    async requestImagesFromLetsBook() {
      if (!this.hasLetsbookIntegration) {
        return;
      }

      this.loading = true;
      const imagesUrl = `${this.settings.letsbookApiId}/hoteis/${this.hotelId}/galerias/${this.apiLocale}`;

      if (!this.apiData[imagesUrl]) {
        try {
          await this.$store.dispatch('loadApiData', imagesUrl);
        } catch {
          this.loading = false;
          return;
        }
      }

      this.letsBookImages = this.apiData[imagesUrl].Hotel.Galeria.map(
        (image) => ({
          type: 'image',
          image,
        }),
      );
      this.loading = false;
    },
    initializeGallery() {
      if (!this.editing) {
        window.lightGallery(this.$refs.gallery, {
          selector: '.imagem',
          download: false,
          counter: false,
        });
      }
    },
    destroyGallery() {
      if (typeof this.lightGallery === 'object') {
        this.lightGallery.destroy(true);
      }
    },
    startItemAlternation() {
      const timeInSecs = Math.max(Number(this.alternateItemsSpeed), 2);

      this.interval = setInterval(() => {
        this.alternateState = !this.alternateState;
      }, timeInSecs * 1000);
    },
    stopItemAlternation() {
      clearInterval(this.interval);
    },
    mediaHref(media) {
      if (media.type === 'video') {
        return media.linkVideo?.value;
      }
      return media.image;
    },
    mediaSrc(media) {
      if (media.type === 'video') {
        const linkMatch = media.linkVideo?.value.match(
          /(youtu\.be\/|youtube\.com\/(watch\?(.*&)?v=|(embed|v)\/))([^?&"'>]+)/,
        );

        if (linkMatch) {
          return `https://img.youtube.com/vi/${linkMatch[5]}/hqdefault.jpg`;
        }

        return '';
      }

      return media.image;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'media',
            label: 'Mídias',
            fields: [
              {
                name: 'media',
                type: 'List',
                itemLabel: 'Mídia {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar mídia',
                fields: [
                  {
                    name: 'type',
                    type: 'VueSelect',
                    label: 'Tipo',
                    options: [
                      {
                        value: 'image',
                        label: 'Imagem',
                      },
                      {
                        value: 'video',
                        label: 'Vídeo',
                      },
                      {
                        value: 'text',
                        label: 'Texto',
                      },
                    ],
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem',
                    isEnabled() {
                      return this.type === 'image';
                    },
                  },
                  {
                    name: 'linkVideo',
                    type: 'Url',
                    label: 'URL do vídeo',
                    availableTypes: ['externalLink'],
                    allowNewTab: false,
                    isEnabled() {
                      return this.type === 'video';
                    },
                  },
                  {
                    name: 'caption',
                    type: 'ShortText',
                    label: 'Legenda (aparece ao expandir)',
                    maxlength: 80,
                    isEnabled() {
                      return this.type === 'image' || this.type === 'video';
                    },
                  },
                  {
                    name: 'alt',
                    type: 'ShortText',
                    label: 'Texto alternativo (para SEO)',
                    isEnabled() {
                      return this.type === 'image' || this.type === 'video';
                    },
                  },
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Título',
                    isEnabled() {
                      return this.type === 'text';
                    },
                  },
                  {
                    name: 'text',
                    type: 'LongText',
                    label: 'Texto',
                    isEnabled() {
                      return this.type === 'text';
                    },
                  },
                  {
                    name: 'titleColor',
                    type: 'ColorPicker',
                    label: 'Cor do título',
                    default: '#000000',
                    isEnabled() {
                      return this.type === 'text';
                    },
                  },
                  {
                    name: 'textColor',
                    type: 'ColorPicker',
                    label: 'Cor do texto',
                    default: '#000000',
                    isEnabled() {
                      return this.type === 'text';
                    },
                  },
                  {
                    name: 'bgColor',
                    type: 'ColorPicker',
                    label: 'Cor de fundo',
                    default: '#ffffff',
                    isEnabled() {
                      return this.type === 'text';
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'loadImagesFromLetsBook',
                type: 'Checkbox',
                label: "Importar imagens do Let's Book",
                isEnabled({ hasLetsbookIntegration }) {
                  return hasLetsbookIntegration;
                },
              },
              {
                name: 'hotelId',
                type: 'VueSelect',
                label: 'Selecione um hotel',
                isEnabled({ loadImagesFromLetsBook }) {
                  return loadImagesFromLetsBook;
                },
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'alternateItems',
                type: 'Checkbox',
                label: 'Habilitar transição automática das mídias',
              },
              {
                name: 'alternateItemsSpeed',
                type: 'Range',
                label: 'Velocidade da transição',
                min: 2,
                max: 20,
                step: 1,
                format: {
                  suffix: 's',
                },
                isEnabled({ alternateItems }) {
                  return alternateItems === true;
                },
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
    ],
    toRemove: ['appearance'],
  },
};
</script>

<style lang="scss">
$lg-path-fonts: '~lightgallery.js/src/fonts';
$lg-path-images: '~lightgallery.js/src/img';

@import 'lightgallery.js/src/sass/lightgallery.scss';

.lg-sub-html {
  font-family: $lb-setting-font;
  font-weight: $lb-normal;
  line-height: 1.5;
}

.lg-actions .lg-icon {
  background-color: #0005;
  padding: 20px;
  border-radius: 50px;
  color: $lb-white !important;
}

.lg-actions .lg-next:hover,
.lg-actions .lg-prev:hover {
  color: #999;
}

@include media-breakpoint-down(md) {
  .lg-actions .lg-icon {
    padding: 10px;
    font-size: $size-normal;
  }
}

.lg-backdrop {
  z-index: 8040;
}

.lg-outer {
  z-index: 8050;
}
</style>

<style scoped lang="scss">
.photos-mosaic-module {
  padding: 0;

  &.loading {
    min-height: 135px;
  }

  .loader {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    background: rgba($gray-50, 0.3);
  }

  .grid-imagens {
    display: flex;
    flex-wrap: wrap;

    .grid-block {
      flex: 1 1 25%;
      max-height: 430px;
      overflow: hidden;
      position: relative;

      &:nth-child(1n + 9) {
        display: none; // hide blocks over 8
      }

      .grid-item:nth-child(2) {
        transition: transform 400ms ease-in-out;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transform: translate(0, 100%);
      }

      &:nth-child(1) .grid-item:nth-child(2) {
        transition-delay: 100ms;
      }
      &:nth-child(2) .grid-item:nth-child(2) {
        transition-delay: 200ms;
      }
      &:nth-child(3) .grid-item:nth-child(2) {
        transition-delay: 300ms;
      }
      &:nth-child(4) .grid-item:nth-child(2) {
        transition-delay: 400ms;
      }
      &:nth-child(5) .grid-item:nth-child(2) {
        transition-delay: 500ms;
      }
      &:nth-child(6) .grid-item:nth-child(2) {
        transition-delay: 600ms;
      }
      &:nth-child(7) .grid-item:nth-child(2) {
        transition-delay: 700ms;
      }
      &:nth-child(8) .grid-item:nth-child(2) {
        transition-delay: 800ms;
      }
    }

    &[data-alternate-state] {
      .grid-block .grid-item:nth-child(2) {
        transform: translate(0, 0);
      }
    }

    @include media-breakpoint-up(md) {
      .grid-block:first-child:nth-last-child(5),
      .grid-block:first-child:nth-last-child(5) ~ .grid-block {
        flex: 1 1 33%;
      }
    }

    .imagem {
      &:not(.cta) {
        border: 0;
        display: block;
        overflow: hidden;
        width: 100%;
        height: 0;
        padding-bottom: 64%;

        &::before {
          content: '';
          display: block;
          background: rgba(0, 0, 0, 0.15);
          opacity: 1;
          position: absolute;
          top: 0;
          left: 0;
          transform: none;
          transition: all 400ms;
          width: 100%;
          height: 100%;
        }

        figure {
          transition: all 200ms;
        }

        &:hover {
          &::before {
            opacity: 0;
          }

          figure {
            transform: scale(1.03);
          }
        }
      }
    }

    [data-type='video'] {
      a:not(.cta) {
        &::before {
          background: rgba(0, 0, 0, 0.6);
        }

        &::after {
          content: '';
          display: block;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 100px;
          opacity: 0.5;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 142.448 142.448' fill='%23fff'%3E%3Cpath d='M142.411 68.9C141.216 31.48 110.968 1.233 73.549.038c-20.361-.646-39.41 7.104-53.488 21.639C6.527 35.65-.584 54.071.038 73.549c1.194 37.419 31.442 67.667 68.861 68.861.779.025 1.551.037 2.325.037 19.454 0 37.624-7.698 51.163-21.676 13.534-13.972 20.646-32.394 20.024-51.871zm-30.798 41.436c-10.688 11.035-25.032 17.112-40.389 17.112-.614 0-1.228-.01-1.847-.029-29.532-.943-53.404-24.815-54.348-54.348-.491-15.382 5.122-29.928 15.806-40.958 10.688-11.035 25.032-17.112 40.389-17.112.614 0 1.228.01 1.847.029 29.532.943 53.404 24.815 54.348 54.348.491 15.382-5.123 29.928-15.806 40.958z'/%3E%3Cpath d='M94.585 67.086 63.001 44.44c-3.369-2.416-8.059-.008-8.059 4.138v45.293c0 4.146 4.69 6.554 8.059 4.138l31.583-22.647c2.834-2.031 2.834-6.244.001-8.276z'/%3E%3C/svg%3E");
        }
      }
    }

    [data-type='text'] {
      height: 100%;
      padding-bottom: 64%; // aspect ratio 25:16
    }

    .text-item {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      height: 100%;
      position: absolute;
      padding: 20px;
      text-align: center;

      &__title {
        font-size: 30px;
        font-weight: $lb-bold;
        line-height: 1.1em;
        text-transform: uppercase;
        color: $lb-black;

        @include media-breakpoint-down(xl) {
          font-size: 24px;
        }
        @include media-breakpoint-down(lg) {
          font-size: $size-normal;
        }
      }

      &__text {
        font-size: 18px;
        font-weight: $lb-medium;
        line-height: 1.3em;
        margin-top: 0.75em;
        color: $lb-black;

        @include media-breakpoint-down(xl) {
          font-size: $size-normal;
        }
        @include media-breakpoint-down(lg) {
          font-size: $size-smaller;
        }
      }
    }
  }

  figure {
    margin: 0;
    padding: 0;
  }

  @include media-breakpoint-down(md) {
    .grid-imagens {
      .grid-block {
        flex: 1 1 33%;
      }
    }
  }

  @include media-breakpoint-down(sm) {
    .grid-imagens {
      .grid-block {
        flex: 1 1 50%;
      }
    }
  }
}

.bg,
.full-bg {
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  font-size: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.full-bg {
  position: absolute;
  top: 0;
  left: 0;
}

.bg img,
.full-bg img {
  opacity: 0;
  visibility: hidden;
  width: 100%;
  height: 100%;
}
</style>
